import { cloneBoard, countPieces, initialBoard, opponent, positionKey } from "./board.js";
import { advance, drawReason, newDrawState } from "./draw.js";
import { applyMove, generateMoves } from "./moves.js";
function pathsEqual(a, b) {
    if (a.length !== b.length)
        return false;
    for (let i = 0; i < a.length; i++)
        if (a[i] !== b[i])
            return false;
    return true;
}
/**
 * A game of draughts under one variant's rules.
 *
 * Pure: no clock, no I/O, no persistence. The server owns time; this owns
 * legality. Both server and client run the same instance of this class, so
 * there is exactly one source of rules truth.
 */
export class Game {
    variant;
    board;
    turn;
    history = [];
    drawState = newDrawState();
    cached = null;
    cacheValid = false;
    constructor(variant, board, turn = "w") {
        this.variant = variant;
        this.board = board ? cloneBoard(board) : initialBoard(variant);
        this.turn = turn;
        this.bumpPosition();
    }
    bumpPosition() {
        const key = positionKey(this.board, this.turn);
        const positions = this.drawState.positions;
        positions.set(key, (positions.get(key) ?? 0) + 1);
    }
    legalMoves() {
        return generateMoves(this.variant, this.board, this.turn);
    }
    /**
     * Find the unique legal move matching from/to.
     *
     * With flying kings a single from/to pair can describe several different
     * capture sequences, so the caller must supply the exact path to
     * disambiguate. Returning undefined for an ambiguous move is deliberate:
     * guessing would silently remove the wrong pieces.
     */
    findLegalMove(from, to, path) {
        const matches = this.legalMoves().filter((m) => m.from === from && m.to === to);
        if (matches.length <= 1)
            return matches[0];
        if (path)
            return matches.find((m) => pathsEqual(m.path, path));
        return undefined;
    }
    /** True when from/to alone is not enough to identify the move. */
    isAmbiguous(from, to) {
        return this.legalMoves().filter((m) => m.from === from && m.to === to).length > 1;
    }
    /** Play a move. Throws if illegal or ambiguous. */
    move(from, to, path) {
        if (this.isOver())
            throw new Error("Game is already over");
        const move = this.findLegalMove(from, to, path);
        if (!move) {
            throw new Error(this.isAmbiguous(from, to)
                ? `Ambiguous move ${from}-${to}: a capture path is required`
                : `Illegal move ${from}-${to}`);
        }
        return this.applyLegal(move);
    }
    applyLegal(move) {
        const mover = this.board[move.from - 1];
        const wasManMove = mover ? !mover.king : false;
        const wasCapture = move.captures.length > 0;
        this.board = applyMove(this.board, move);
        this.history.push(move);
        this.turn = opponent(this.turn);
        advance(this.variant, this.drawState, this.board, wasManMove, wasCapture);
        this.bumpPosition();
        this.cacheValid = false;
        return move;
    }
    /** Current outcome, or null if the game is still in progress. */
    outcome() {
        if (this.cacheValid)
            return this.cached;
        this.cached = this.computeOutcome();
        this.cacheValid = true;
        return this.cached;
    }
    computeOutcome() {
        // FMJD 7.2.2 / 7.2.3 — no pieces, or no legal move, loses.
        //
        // Checked BEFORE the draw rules, which implements the 6.3 exception: if
        // the last of the 16 moves produces a blocked or piece-less opponent, the
        // win takes precedence over the draw.
        if (countPieces(this.board, this.turn) === 0) {
            return { result: this.turn === "w" ? "black" : "white", reason: "no-pieces" };
        }
        if (this.legalMoves().length === 0) {
            return { result: this.turn === "w" ? "black" : "white", reason: "blocked" };
        }
        const key = positionKey(this.board, this.turn);
        const draw = drawReason(this.variant, this.drawState, key);
        if (draw)
            return { result: "draw", reason: draw };
        return null;
    }
    /** Convenience: just the result, or null. */
    result() {
        return this.outcome()?.result ?? null;
    }
    isOver() {
        return this.outcome() !== null;
    }
}
